/**
 * R2R listing badge definitions.
 *
 * Single source of truth for the badges rendered on listing cards and on the
 * listing detail page (see the ListingBadges component). Adding a new badge is
 * a one-line addition to the LISTING_BADGES array below.
 *
 * Each badge entry:
 * @property {string} id        unique badge id (also used as React key)
 * @property {('text'|'icon')} kind  how the badge renders
 * @property {string} labelId   i18n key. For 'text' badges it is the visible
 *                              label; for 'icon' badges it is the aria-label/title.
 * @property {string?} iconId   icon registry key (required when kind === 'icon').
 *                              The ListingBadges component maps this to a component,
 *                              so this config stays free of component imports
 *                              (avoids circular dependencies).
 * @property {string[]} publicDataKeys  publicData keys the badge reads. Used to
 *                              build the sparse fieldset for the search query so the
 *                              data actually reaches the listing cards (see
 *                              LISTING_BADGE_LISTING_FIELDS below).
 * @property {(publicData: Object) => boolean} isActive  predicate that decides
 *                              whether the badge shows for a given listing's publicData.
 */

/**
 * True when shipping is enabled and every first-item fee (default + zones) is
 * literally $0, and every additional is 0 or unset. A missing one-item price is
 * not treated as free.
 *
 * @param {Object} publicData
 * @returns {boolean}
 */
const isFreeFeePair = source => {
  const additional = source?.shippingPriceInSubunitsAdditionalItems;
  return source?.shippingPriceInSubunitsOneItem === 0 && (additional === 0 || additional == null);
};

export const isFreeShipping = publicData => {
  if (publicData?.shippingEnabled !== true) {
    return false;
  }
  const rates = publicData?.shippingRates;
  if (rates && typeof rates === 'object' && rates.default && typeof rates.default === 'object') {
    const zones = Array.isArray(rates.zones) ? rates.zones : [];
    return isFreeFeePair(rates.default) && zones.every(isFreeFeePair);
  }
  return isFreeFeePair(publicData);
};

export const LISTING_BADGES = [
  {
    id: 'wysiwyg',
    kind: 'text',
    labelId: 'ListingBadges.wysiwyg',
    publicDataKeys: ['wysiwyg_coral'],
    isActive: publicData => publicData?.wysiwyg_coral === 'wysiwyg_yes',
  },
  {
    id: 'freeShipping',
    kind: 'text',
    labelId: 'ListingBadges.freeShipping',
    publicDataKeys: [
      'shippingEnabled',
      'shippingPriceInSubunitsOneItem',
      'shippingPriceInSubunitsAdditionalItems',
      'shippingRates',
    ],
    isActive: isFreeShipping,
  },
  {
    id: 'shipping',
    kind: 'icon',
    iconId: 'shipping',
    labelId: 'ListingBadges.shipping',
    publicDataKeys: ['shippingEnabled', 'shippingRates'],
    isActive: publicData => publicData?.shippingEnabled === true && !isFreeShipping(publicData),
  },
];

/**
 * The `publicData.*` sparse fieldset entries required by the badges, derived from
 * LISTING_BADGES. The search endpoint (SearchPage.duck.js) only returns the
 * publicData keys it explicitly requests, so these must be added to
 * `fields.listing` for badges to render on listing cards.
 *
 * @type {string[]}
 */
export const LISTING_BADGE_LISTING_FIELDS = [
  ...new Set(
    LISTING_BADGES.flatMap(badge => badge.publicDataKeys || []).map(key => `publicData.${key}`)
  ),
];
